// lab.* (lane X's lab test conditions, nodded by the coordinator 2026-10-16; ops resume x-node.md checkpoint 56): on
// staging's lab mode only, Radish staff put a lab-listed node, register or the cloud's view of a lab register into a
// simulated condition (looks offline, slow, cut off, a clock offset, a disk under its floor) for a QA test, the way the
// chaos suite does. Our own code enters the condition; nothing is stopped and nothing is filled. Every condition ends by
// itself within LAB_CONDITION_TTL.maxSeconds, enforced at every side, and every set and clear is audited.
//   - The commands are cloud only and refused outright unless the cloud runs staging's lab mode (RADISH_LAB_MODE, D10).
//   - Every device a condition touches must be on the lab devices list (`node:<serial>`, `device:<id>`; fleet.lab_devices, seeded from RADISH_LAB_DEVICES).
//   - A node takes a condition only from a signed beacon answer, and only when its binding carries `lab: true`.
// A register's own conditions (its clock, printer, card terminal) are entered only by qa-staging register builds
// (invariant D11): it receives each as a `labCondition` device command, and only a build declaring LAB_CONDITION_READER does.
import { z } from 'zod';
import { defineError } from '../../define/errors.ts';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { DeviceId, IsoInstant, LocationId } from '../../primitives/codecs.ts';

const LAB_CLOUD = "Lab conditions are staging's alone: the cloud signs each one for a lab node, and keeps their record.";

/** How long a condition may last: it reverts by itself at its expiry, even if the clear never arrives (at most 30 minutes). */
export const LAB_CONDITION_TTL = { minSeconds: 10, maxSeconds: 1800, defaultSeconds: 300 } as const;

/**
 * `node_offline` can never be cleared early (the node is not listening to the cloud while it holds), so it lasts at most
 * 10 minutes (coordinator's security review, 2026-10-16). Commander shows it as "can't be cleared early; ends at …".
 */
export const LAB_OFFLINE_MAX_SECONDS = 600;

/** At most this many conditions may be active at one location at once. */
export const LAB_CONDITIONS_PER_LOCATION = 16;

/** The slow node's delay, and the clock offsets a condition may set. */
export const LAB_LIMITS = { maxDelayMs: 5000, maxClockOffsetMs: 3_600_000 } as const;

/** Every register condition sets the register's own clock, printer or card terminal; only qa-staging builds enter them (D11). */
const RegisterConditions = [
  /** The register's clock source reads this far off. */
  z.object({
    kind: z.literal('register_clock_offset'), deviceId: DeviceId,
    offsetMs: z.int().min(-LAB_LIMITS.maxClockOffsetMs).max(LAB_LIMITS.maxClockOffsetMs).refine((ms) => ms !== 0, 'an offset other than zero'),
  }).strict(),
  /** The register's printer driver answers that the printer is offline. */
  z.object({ kind: z.literal('printer_offline'), deviceId: DeviceId }).strict(),
  /** The register's card terminal driver answers that the terminal is not connected. */
  z.object({ kind: z.literal('terminal_unreachable'), deviceId: DeviceId }).strict(),
] as const;

/** A register's own condition, as its `labCondition` device command carries it. */
export const LabRegisterCondition = z.discriminatedUnion('kind', [...RegisterConditions]).meta({ id: 'LabRegisterCondition' });
export type LabRegisterCondition = z.output<typeof LabRegisterCondition>;

/**
 * A simulated condition, and where it takes effect (`side`): a node condition is entered by the location's node (its agent
 * and server), a cloud condition by the cloud. Each kind takes exactly its own arguments.
 */
export const LabCondition = z.discriminatedUnion('kind', [
  /** The node's agent pauses its beacon and hello, and its server pauses replication with the cloud. */
  z.object({ kind: z.literal('node_offline') }).strict(),
  /** The node's server answers its units with 503 (its health probe still answers). */
  z.object({ kind: z.literal('node_unreachable') }).strict(),
  /** The node's server refuses this one register's calls, its pulls and pushes among them. */
  z.object({ kind: z.literal('device_cut_off_from_node'), deviceId: DeviceId }).strict(),
  /** The node holds its upstream outbox: nothing it holds goes to the cloud (it still takes the cloud's feed). */
  z.object({ kind: z.literal('sync_stalled') }).strict(),
  /** The node's receiver answers this register's pushes with `update_required`: held by the register, never lost. */
  z.object({ kind: z.literal('sync_held_for_device'), deviceId: DeviceId }).strict(),
  /** The node's server answers its units this much later. */
  z.object({ kind: z.literal('node_slow'), delayMs: z.int().min(100).max(LAB_LIMITS.maxDelayMs) }).strict(),
  /** The node's clock reads this far off (its server's clock source, and the offset its beacon reports). */
  z.object({
    kind: z.literal('node_clock_offset'),
    offsetMs: z.int().min(-LAB_LIMITS.maxClockOffsetMs).max(LAB_LIMITS.maxClockOffsetMs).refine((ms) => ms !== 0, 'an offset other than zero'),
  }).strict(),
  /**
   * The node's write guard reads a raised floor, so it refuses commands and pushes as below its floor (chaos C12). Only
   * the threshold moves: nothing is written to the disk, and the real floor still stands under it.
   */
  z.object({ kind: z.literal('node_disk_floor') }).strict(),
  /** The cloud refuses this register's sync and API calls. */
  z.object({ kind: z.literal('device_cut_off_from_cloud'), deviceId: DeviceId }).strict(),
  ...RegisterConditions,
]).meta({ id: 'LabCondition' });
export type LabCondition = z.output<typeof LabCondition>;
export type LabConditionKind = LabCondition['kind'];

/** Where each condition takes effect. */
export const LAB_CONDITION_SIDE: Readonly<Record<LabConditionKind, 'node' | 'cloud' | 'device'>> = {
  node_offline: 'node', node_unreachable: 'node', device_cut_off_from_node: 'node', sync_stalled: 'node', sync_held_for_device: 'node',
  node_slow: 'node', node_clock_offset: 'node', node_disk_floor: 'node', device_cut_off_from_cloud: 'cloud',
  register_clock_offset: 'device', printer_offline: 'device', terminal_unreachable: 'device',
};

/** The register a condition touches, besides its node (each must be on the lab list too). */
export const labConditionDevice = (c: LabCondition): string | null => ('deviceId' in c ? c.deviceId : null);

/** What a condition is entered by: the location's node (by its serial), the cloud, or the register itself (by its id). */
export const LabTarget = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('node'), id: z.string().regex(/^RN-[A-Z0-9]{6,20}$/) }).strict(),
  z.object({ kind: z.literal('cloud') }).strict(),
  z.object({ kind: z.literal('device'), id: DeviceId }).strict(),
]).meta({ id: 'LabTarget' });

export const LabConditionState = z.enum(['requested', 'applied', 'cleared', 'expired']).meta({ id: 'LabConditionState' });

export const LabConditionView = z.object({
  conditionId: z.uuid(),
  target: LabTarget,
  condition: LabCondition,
  state: LabConditionState,
  requestedAt: IsoInstant,
  /** When the node reported it entered the condition (the cloud's own: when it was set); null until then. */
  appliedAt: IsoInstant.nullable(),
  expiresAt: IsoInstant,
  clearedAt: IsoInstant.nullable(),
}).meta({ id: 'LabConditionView' });

/** The cloud is not running staging's lab mode: no lab condition exists here at all. */
export const LabModeOff = defineError({
  code: 'lab.mode_off', httpStatus: 403, message: 'errors.lab.mode_off', retryable: false, details: z.object({}),
});

/** A device the condition touches is not on the lab devices list (fleet.lab_devices), or the node is not this location's. */
export const LabNotListed = defineError({
  code: 'lab.not_listed', httpStatus: 403, message: 'errors.lab.not_listed', retryable: false,
  details: z.object({ devices: z.array(z.string()) }),
});

/** The condition is entered on another side than the target names (a node condition aimed at the cloud, or the reverse). */
export const LabWrongTarget = defineError({
  code: 'lab.wrong_target', httpStatus: 422, message: 'errors.lab.wrong_target', retryable: false,
  details: z.object({ kind: z.string(), side: z.enum(['node', 'cloud', 'device']) }),
});

/** As many conditions are active at the location as may be. */
export const LabTooMany = defineError({
  code: 'lab.too_many', httpStatus: 429, message: 'errors.lab.too_many', retryable: true,
  details: z.object({ locationId: LocationId, active: z.int() }),
});

/** No condition has this id at this location. */
export const LabConditionNotFound = defineError({
  code: 'lab.condition_not_found', httpStatus: 404, message: 'errors.lab.condition_not_found', retryable: false,
  details: z.object({ conditionId: z.uuid() }),
});

/** The location of any account Radish staff act at (`staffAnyLocation`), as Commander's commands name it. */
const At = { accountId: z.uuid(), locationId: LocationId };

export const SetLabCondition = defineCommand({
  name: 'lab.setCondition',
  summary: "Put a lab-listed node, or the cloud's view of a lab register, into a simulated condition for a QA test, on staging's lab mode only; it ends by itself at its expiry (at most 30 minutes).",
  input: z.object({
    ...At,
    target: LabTarget,
    condition: LabCondition,
    ttlSeconds: z.int().min(LAB_CONDITION_TTL.minSeconds).max(LAB_CONDITION_TTL.maxSeconds).default(LAB_CONDITION_TTL.defaultSeconds),
  }).refine((i) => i.condition.kind !== 'node_offline' || i.ttlSeconds <= LAB_OFFLINE_MAX_SECONDS, {
    path: ['ttlSeconds'], message: `node_offline lasts at most ${String(LAB_OFFLINE_MAX_SECONDS)} seconds (it cannot be cleared early)`,
  }),
  output: z.object({ conditionId: z.uuid(), expiresAt: IsoInstant }),
  permission: 'lab.manage',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: LAB_CLOUD,
  emits: [],
  errors: [LabModeOff, LabNotListed, LabWrongTarget, LabTooMany],
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  staffAnyLocation: true,
});

export const ClearLabCondition = defineCommand({
  name: 'lab.clearCondition',
  summary: "End one lab condition now (a node that looks offline hears it only at its expiry, since it is not listening).",
  input: z.object({ ...At, conditionId: z.uuid() }),
  output: z.object({ conditionId: z.uuid(), cleared: z.boolean() }),
  permission: 'lab.manage',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: LAB_CLOUD,
  emits: [],
  errors: [LabModeOff, LabConditionNotFound],
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  staffAnyLocation: true,
});

export const ClearAllLabConditions = defineCommand({
  name: 'lab.clearAll',
  summary: "End every lab condition active at one location now.",
  input: z.object(At),
  output: z.object({ cleared: z.array(z.uuid()) }),
  permission: 'lab.manage',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: LAB_CLOUD,
  emits: [],
  errors: [LabModeOff],
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  staffAnyLocation: true,
});

export const LabConditions = defineQuery({
  name: 'lab.conditions',
  summary: "The lab conditions set at one location in the last day, newest first, each with its state (requested, applied, cleared or expired).",
  input: z.object({ locationId: LocationId }),
  output: z.object({ labMode: z.boolean(), conditions: z.array(LabConditionView) }),
  permission: 'lab.manage',
  availability: 'cloud',
  cloudReason: LAB_CLOUD,
  scope: 'platform',
  errors: [],
  agent: { expose: false },
});

// ---- The lab devices list (ops plan 2026-10-17-commander-redesign §4.5b, step 5d; coordinator-nodded 2026-10-17) -------
// The devices QA may break on purpose, kept in the cloud's table instead of the RADISH_LAB_DEVICES setting: each added
// or removed with a reason, both audited, rows never deleted. Only on staging's lab mode; a production cloud ignores the
// table entirely (config refuses lab mode there, D10) and these answer lab.mode_off.

/** A lab device as the list names it: an RMS router id, a hub's serial, or a register's device id (kept lower-case). */
export const LabDeviceRef = z.string().trim().regex(/^(?:rms:[1-9][0-9]{0,15}|node:RN-[A-Z0-9]{6,20}|device:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/)
  .meta({ id: 'LabDeviceRef' });

const LabDeviceReason = z.string().trim().min(1).max(200);

/** The device is already on the lab list. */
export const LabDeviceListed = defineError({
  code: 'lab.device_listed', httpStatus: 409, message: 'errors.lab.device_listed', retryable: false,
  details: z.object({ device: z.string() }),
});

/** No listed lab device has this id (it was never added, or it was removed). */
export const LabDeviceNotFound = defineError({
  code: 'lab.device_not_found', httpStatus: 404, message: 'errors.lab.device_not_found', retryable: false,
  details: z.object({ labDeviceId: z.uuid() }),
});

const LAB_DEVICES_CLOUD = "The lab devices list is staging's alone: the cloud's lab policy reads it to approve lab actions.";

export const AddLabDevice = defineCommand({
  name: 'lab.devices.add',
  summary: 'Put a device on the lab list (a register, a hub or a router QA may break on purpose), saying why. Staging only.',
  input: z.object({ labDeviceId: z.uuid(), device: LabDeviceRef, reason: LabDeviceReason }),
  output: z.object({ labDeviceId: z.uuid() }),
  permission: 'lab.devices.manage',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: LAB_DEVICES_CLOUD,
  emits: [],
  errors: [LabModeOff, LabDeviceListed],
  risk: 'low',
  dryRun: true,
  agent: { expose: false },
});

export const RemoveLabDevice = defineCommand({
  name: 'lab.devices.remove',
  summary: 'Take a device off the lab list, saying why. From then on no lab action may touch it. Staging only.',
  input: z.object({ labDeviceId: z.uuid(), reason: LabDeviceReason }),
  output: z.object({ labDeviceId: z.uuid() }),
  permission: 'lab.devices.manage',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: LAB_DEVICES_CLOUD,
  emits: [],
  errors: [LabModeOff, LabDeviceNotFound],
  risk: 'low',
  dryRun: true,
  agent: { expose: false },
});

export const LabDeviceView = z.object({
  labDeviceId: z.uuid(),
  device: z.string(),
  kind: z.enum(['register', 'hub', 'router']),
  /** The register's name, the hub's store, or the router's RMS name, when known. */
  label: z.string().nullable(),
  locationName: z.string().nullable(),
  reason: z.string(),
  addedBy: z.string(),
  addedAt: IsoInstant,
}).meta({ id: 'LabDeviceView' });

export const ListLabDevices = defineQuery({
  name: 'lab.devices.list',
  summary: "The devices on staging's lab list, each with what it is, why it was added, by whom and when.",
  input: z.object({}),
  output: z.object({ devices: z.array(LabDeviceView) }),
  permission: 'lab.manage',
  availability: 'cloud',
  cloudReason: LAB_DEVICES_CLOUD,
  scope: 'platform',
  errors: [LabModeOff],
  agent: { expose: false },
});

export const labCommands = [SetLabCondition, ClearLabCondition, ClearAllLabConditions, AddLabDevice, RemoveLabDevice] as const;
export const labQueries = [LabConditions, ListLabDevices] as const;
